<template>
  <SfDropdown
    :dropdown-id="dropdowns.PERIOD_PICKER"
    :is-open="shouldShowDropdown"
    :is-full-width="true"
    :is-disabled="disabled || isLoading"
    :no-options-styling="true"
    :unstyled-trigger="true"
    :select-class-names="selectClassNames"
    :menu-class-names="menuClassNames"
    align-option="l"
    class="period-picker-sidebar-trigger"
    @dropdown-trigger-clicked="handleToggleDropdown"
    @dropdown-closed="handleClose"
  >
    <template #trigger>
      <!--
        This is a plain div with sf-button classes rather than SfButtonDs
        because the previous/next arrows below are themselves buttons, and
        buttons can't nest inside buttons.

        sf-button.scss compiles after Tailwind's utilities, so its own
        classes (justify-center, gap-0.5, px-2, ...) win over plain utility
        overrides here unless marked !important. The 14px/2px spacing below
        matches the design mockup's measurements.
      -->
      <div
        :aria-disabled="disabled ? 'true' : undefined"
        :role="disabled ? 'button' : undefined"
        :tabindex="disabled ? 0 : undefined"
        :class="[
          'tw-group/period-picker-trigger period-picker-trigger-box',
          'sf-button sf-button--secondary sf-button--md',
          'tw-w-full',
          '!tw-justify-between !tw-pr-[14px] !tw-gap-0.5',
          `group-[[data-left-drawer='collapsed']]/main-wrapper:!tw-justify-center`,
          `group-[[data-left-drawer='collapsed']]/main-wrapper:!tw-p-0`,
          `group-[[data-left-drawer='collapsed']]/main-wrapper:!tw-pr-0`,
          { 'sf-button--icon-only': leftCollapsed },
        ]"
      >
        <span
          class="
            tw-flex tw-items-center tw-gap-1 tw-min-w-0
            group-[[data-left-drawer='collapsed']]/main-wrapper:tw-hidden
          "
        >
          <span class="tw-truncate">{{ periodName }}</span>
          <i
            v-if="showVisibilityIcon"
            class="fa-solid fa-eye-slash tw-text-sf-neutral-light-1 tw-shrink-0"
          ></i>
        </span>
        <div class="tw-flex tw-items-center tw-gap-0.5 tw-shrink-0">
          <div
            v-if="!disabled"
            class="
              tw-flex tw-items-center tw-gap-0.5
              group-[[data-left-drawer='collapsed']]/main-wrapper:tw-hidden
            "
          >
            <span
              v-for="arrow in periodArrows"
              :key="arrow.key"
              class="
                !tw-opacity-0
                group-hover/period-picker-trigger:!tw-opacity-100
                focus-within:!tw-opacity-100
              "
            >
              <SfButtonDs
                variant="tertiary"
                size="sm"
                icon-only
                :icon-before="arrow.iconBefore"
                class="!tw-w-5 !tw-h-5 !tw-text-[12px]"
                :aria-label="arrow.label"
                :data-testid="arrow.testid"
                :disabled="!arrow.ledgerId"
                @click.stop.prevent="triggerNavigation(arrow.ledgerId)"
              />
            </span>
          </div>
          <i v-if="isLoading" class="fa fa-spin fa-spinner"></i>
          <IconCalendar
            v-else
            class="
              period-picker-calendar-icon
              tw-w-4 tw-h-4 tw-shrink-0 tw-translate-x-0.5
              group-[[data-left-drawer='collapsed']]/main-wrapper:tw-translate-x-0
            "
          />
        </div>
      </div>
    </template>

    <template #menu>
      <PeriodPicker
        v-if="shouldShowDropdown"
        :current-ledger-id="currentLedgerId"
        :ledgers-per-year="ledgersPerYear"
        :years="years"
        :new-layout="true"
        @change-compare-period="triggerNavigation"
        @close-period-picker="handleClose"
      />
    </template>
  </SfDropdown>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import IconCalendar from '@app/shared/assets/icons/svg/icon-calendar.svg?component'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import PeriodPicker from './period-picker.vue'
import { usePeriodPicker } from '../stores/period-picker'
import { buildPickerTriggerUrl } from '../utils'
import { useDrawer } from '@app/shared/pinia/drawer'
import { useDropdown } from '@app/shared/pinia/dropdown'
import { dropdowns } from '@app/shared/types/dropdown'

defineOptions({
  name: 'PeriodPickerSidebarTrigger',
})

type EnabledProps = {
  disabled?: false
  period: `${number}${number}/${number}${number}${number}${number}`
  periodName: string
  setCurrentLedger: boolean
  url: string
  isVisibleForCustomer: boolean
  previousLedgerId?: string
  nextLedgerId?: string
}

type DisabledProps = {
  disabled: true
  periodName: string
}

const props = defineProps<EnabledProps | DisabledProps>()

const dropdownStore = useDropdown()

const { leftCollapsed } = storeToRefs(useDrawer())

const { currentLedgerId, hasRequiredData, isLoading, ledgersPerYear, years } =
  storeToRefs(usePeriodPicker())

const { requestData } = usePeriodPicker()

const isScopedDropdownOpen = computed(
  () => dropdownStore.currentDropdownId === dropdowns.PERIOD_PICKER
)

const shouldShowDropdown = computed(
  () => isScopedDropdownOpen.value && hasRequiredData.value
)

const showVisibilityIcon = computed(
  () => !props.disabled && !props.isVisibleForCustomer
)

const periodArrows = computed(() => [
  {
    key: 'previous',
    ledgerId: props.disabled ? undefined : props.previousLedgerId,
    label: window.I18n.t('navigation.previous_period'),
    testid: 'period-picker-previous-period',
    iconBefore: 'arrow-left',
  },
  {
    key: 'next',
    ledgerId: props.disabled ? undefined : props.nextLedgerId,
    label: window.I18n.t('navigation.next_period'),
    testid: 'period-picker-next-period',
    iconBefore: 'arrow-right',
  },
])

// SfDropdown's select box always renders its own border/background/shadow
// (from silverfin-ui-kit, not in this repo), even with unstyled-trigger —
// strip it so it doesn't double up with the sf-button box above. Its :active
// state sets the border as a shorthand (width+style+color) with its own
// !important, at higher specificity than our unscoped !tw-border-0 (which has
// no pseudo-class). The color half is already matched by active:!tw-border-
// transparent below, but border-width needs its own active-scoped override too
// — otherwise it silently jumps from 0 to 1px while pressed (no transition on
// this element, so it's an instant, visible size change), shrinking the box
// nested inside it.
const selectClassNames = [
  '!tw-border-0',
  '!tw-bg-transparent',
  '!tw-p-0',
  '!tw-rounded-none',
  '!tw-shadow-none',
  'active:!tw-bg-transparent',
  'active:!tw-border-transparent',
  'active:!tw-border-0',
  'tw-flex',
  'tw-justify-center',
]

const menuClassNames = [
  'tw-absolute',
  'tw-z-[10000]',
  'tw-top-full',
  'tw-left-0',
  'tw-w-full',
  "group-[[data-left-drawer='collapsed']]/main-wrapper:tw-w-[var(--left-drawer-width,340px)]",
]

const mappingScreenRegexp = /\/accounts\/(table_)?edit_mapping/

function handleToggleDropdown() {
  if (props.disabled || isLoading.value) {
    return
  }

  if (!isScopedDropdownOpen.value && !hasRequiredData.value) {
    requestData(props.url)
  }

  dropdownStore.toggleDropdown(dropdowns.PERIOD_PICKER)
}

function handleClose() {
  dropdownStore.closeDropdown()
}

function triggerNavigation(ledgerId?: string) {
  if (props.disabled || !ledgerId) return

  let newUrl = window.location.href.replace(
    /\/ledgers\/[0-9]+/,
    `/ledgers/${ledgerId}`
  )

  /*
  When the mapping can be skipped, we want navigation to go directly to
  the workflows page, even if we are currently on the mapping screen.
  If the mapping on the ledger we are navigating to will be required,
  the controller will redirect us to the mapping page anyway.
  */
  if (mappingScreenRegexp.test(newUrl)) {
    newUrl = newUrl.replace(mappingScreenRegexp, '/workflows')
  }

  window.Turbolinks.visit(
    props.setCurrentLedger
      ? buildPickerTriggerUrl(newUrl, 'current_ledger_id', ledgerId)
      : newUrl
  )
}
</script>

<style scoped>
.period-picker-sidebar-trigger :deep(.sf-dropdown2-wrapper::after),
.period-picker-sidebar-trigger :deep([class*='sf-dropdown2-wrapper']::after) {
  display: none;
}

/*
  Every icon from sf-svg-loader.js gets an unconditional margin-bottom: -2px
  for inline-text baseline alignment, which pushes this icon off-center in
  our flex row instead. There's no prop to opt out, so :deep() it is.
*/
.period-picker-sidebar-trigger :deep(.period-picker-calendar-icon) {
  margin-bottom: 0 !important;
}

/*
  The arrow buttons are nested inside this box (they must sit inside the
  bordered area, and buttons can't nest in buttons otherwise). CSS :hover and
  :active cascade to ancestors regardless of @click.stop on the arrow, so
  hovering/pressing an arrow was also flipping this whole box into its own
  hover/active colors. Only the arrow itself should show press/hover feedback,
  so force the box back to its rest-state colors whenever the interaction is
  on one of the nested arrow buttons rather than the box itself.
*/
.period-picker-sidebar-trigger
  :deep(.period-picker-trigger-box:has(button:hover)),
.period-picker-sidebar-trigger
  :deep(.period-picker-trigger-box:has(button:active)) {
  background-color: #fff !important;
  border-color: var(--color-space-subtle, #cbd5e1) !important;
  color: var(--color-space-light, #475569) !important;
}
</style>
